import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { FiMail, FiSend } from 'react-icons/fi';
import api from '../config/api';
import Modal from './Modal';
import LoadingButton from './LoadingButton';
import { appAlert } from '../utils/appDialog';
import './SendTemplateEmailModal.css';

function templateListKey(t) {
  return t?.id || t?.action_id || '';
}

function recipientPayloadBase(recipient, actionId, extraVars) {
  const base = {
    action_id: actionId,
    variables: {
      first_name: recipient?.firstName || '',
      last_name: recipient?.lastName || '',
      email: recipient?.email || '',
      ...extraVars,
    },
  };
  if (recipient?.userId) base.user_id = recipient.userId;
  else if (recipient?.registerCandidateId) {
    base.register_candidate_id = recipient.registerCandidateId;
  } else if (recipient?.email) {
    base.email = recipient.email;
  }
  return base;
}

function recipientDisplay(recipient) {
  return (
    recipient?.displayName ||
    [recipient?.firstName, recipient?.lastName].filter(Boolean).join(' ') ||
    recipient?.email ||
    'recipient'
  );
}

/**
 * Canned email: pick a Settings → Email template and send to one or many recipients.
 *
 * Props:
 * - open, onClose
 * - recipient: single { userId?, registerCandidateId?, email?, firstName?, lastName?, displayName? }
 * - recipients: same shape, array (bulk). Takes precedence when non-empty.
 * - onSent?: ({ sent, failed }) => void after bulk/single complete
 */
export default function SendTemplateEmailModal({ open, onClose, recipient, recipients, onSent }) {
  const [templateKey, setTemplateKey] = useState('');
  const [extraVars, setExtraVars] = useState({});

  const recipientList = useMemo(() => {
    if (Array.isArray(recipients) && recipients.length > 0) return recipients;
    if (recipient) return [recipient];
    return [];
  }, [recipient, recipients]);

  const isBulk = recipientList.length > 1;
  const primary = recipientList[0] || null;

  const { data, isLoading, isError, error } = useQuery(
    ['email-manual-templates'],
    () => api.get('/email/templates').then((r) => r.data),
    { enabled: open, staleTime: 60_000 }
  );

  const templates = useMemo(
    () => (Array.isArray(data?.templates) ? data.templates : []),
    [data?.templates]
  );

  useEffect(() => {
    if (!open) {
      setTemplateKey('');
      setExtraVars({});
      return;
    }
    if (templates.length && !templateKey) {
      const preferred =
        templates.find((t) => t.action_id === 'user_signup' || t.id === 'user_signup') ||
        templates.find(
          (t) => t.action_id === 'candidate_onboarded' || t.id === 'candidate_onboarded'
        ) ||
        templates.find(
          (t) => t.action_id === 'register_welcome' || t.id === 'register_welcome'
        ) ||
        templates[0];
      if (preferred) setTemplateKey(templateListKey(preferred));
    }
  }, [open, templates, templateKey]);

  const selected = useMemo(
    () => templates.find((t) => templateListKey(t) === templateKey) || null,
    [templates, templateKey]
  );

  const actionId = selected?.action_id || selected?.id || '';

  const recipientLabel = isBulk
    ? `${recipientList.length} register contacts`
    : recipientDisplay(primary);

  const payloadBase = useMemo(() => {
    if (!primary || !actionId) return null;
    return recipientPayloadBase(primary, actionId, extraVars);
  }, [actionId, primary, extraVars]);

  const { data: preview, isFetching: previewLoading } = useQuery(
    ['email-preview', payloadBase],
    () => api.post('/email/preview-template', payloadBase).then((r) => r.data),
    {
      enabled:
        open &&
        Boolean(actionId) &&
        Boolean(primary?.userId || primary?.registerCandidateId || primary?.email),
      keepPreviousData: true,
    }
  );

  const sendMutation = useMutation(
    async () => {
      if (!actionId || recipientList.length === 0) {
        throw new Error('No template or recipients selected');
      }
      const results = { sent: 0, failed: [] };
      for (const r of recipientList) {
        if (!r?.email && !r?.userId && !r?.registerCandidateId) {
          results.failed.push({
            id: r?.registerCandidateId || r?.userId,
            error: 'Missing recipient',
          });
          continue;
        }
        try {
          await api.post('/email/send-template', recipientPayloadBase(r, actionId, extraVars));
          results.sent += 1;
        } catch (e) {
          results.failed.push({
            id: r?.registerCandidateId || r?.userId || r?.email,
            email: r?.email,
            error: e.response?.data?.error || e.response?.data?.reason || e.message,
          });
        }
      }
      return results;
    },
    {
      onSuccess: (results) => {
        const { sent, failed } = results || { sent: 0, failed: [] };
        if (failed.length === 0) {
          void appAlert(
            isBulk
              ? `Sent email to ${sent} recipient(s).`
              : `Email sent to ${primary?.email || 'recipient'}.`
          );
        } else {
          void appAlert(
            `Sent ${sent}; failed ${failed.length}.` +
              (failed[0]?.error ? ` First error: ${failed[0].error}` : '')
          );
        }
        onSent?.(results);
        onClose?.();
      },
      onError: (e) =>
        void appAlert(e.response?.data?.error || e.response?.data?.reason || e.message),
    }
  );

  const optionalVars = (selected?.variables || []).filter(
    (v) => !['first_name', 'last_name', 'email', 'app_name', 'role'].includes(v)
  );

  const canSend =
    Boolean(actionId) &&
    Boolean(data?.enabled) &&
    Boolean(data?.smtp_configured) &&
    recipientList.length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Send template email"
      contentClassName="send-email-modal"
      overlayClassName="send-email-modal-overlay"
    >
      <div className="send-email-modal-body">
        <header className="send-email-modal-header">
          <h2>
            <FiMail aria-hidden /> Send email
            {isBulk ? ` (${recipientList.length})` : ''}
          </h2>
          <p className="form-hint" style={{ margin: 0 }}>
            Choose a canned template from Settings → Email and send it to{' '}
            <strong>{recipientLabel}</strong>
            {!isBulk && primary?.email ? ` (${primary.email})` : ''}.
            {isBulk ? (
              <>
                {' '}
                The same template is sent to each selected contact (personal vars filled per person).
              </>
            ) : null}
          </p>
          {isBulk && (
            <ul className="send-email-bulk-list">
              {recipientList.slice(0, 8).map((r) => (
                <li key={r.registerCandidateId || r.userId || r.email}>
                  {recipientDisplay(r)}
                  {r.email ? ` · ${r.email}` : ''}
                </li>
              ))}
              {recipientList.length > 8 && (
                <li className="form-hint">…and {recipientList.length - 8} more</li>
              )}
            </ul>
          )}
        </header>

        {isLoading && <p className="form-hint">Loading templates…</p>}
        {isError && (
          <p className="error">
            {error?.response?.data?.error || error?.message || 'Failed to load templates'}
          </p>
        )}

        {!isLoading && !isError && (
          <>
            {!data?.enabled && (
              <p className="form-hint send-email-warn">
                Outbound email is disabled. Enable it in Settings → Email before sending.
              </p>
            )}
            {!data?.smtp_configured && (
              <p className="form-hint send-email-warn">
                SMTP is not configured. Add host and from address under Settings → Email.
              </p>
            )}

            <div className="form-group">
              <label htmlFor="canned-email-template">Email template</label>
              <select
                id="canned-email-template"
                value={templateKey}
                onChange={(e) => {
                  setTemplateKey(e.target.value);
                  setExtraVars({});
                }}
              >
                {templates.length === 0 && <option value="">No templates available</option>}
                {templates.map((t) => (
                  <option key={templateListKey(t)} value={templateListKey(t)}>
                    {t.custom ? `${t.label} (custom)` : t.label}
                  </option>
                ))}
              </select>
              {selected?.description && (
                <small className="form-hint">{selected.description}</small>
              )}
            </div>

            {optionalVars.length > 0 && (
              <div className="send-email-extra-vars">
                <h4>Optional fields</h4>
                <p className="form-hint">
                  Fill only if this template needs them (leave blank otherwise). Applied to all
                  recipients.
                </p>
                <div className="send-email-extra-grid">
                  {optionalVars.map((key) => (
                    <div className="form-group" key={key}>
                      <label htmlFor={`canned-var-${key}`}>{key}</label>
                      <input
                        id={`canned-var-${key}`}
                        value={extraVars[key] || ''}
                        onChange={(e) =>
                          setExtraVars((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                        placeholder={`{{${key}}}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="send-email-preview">
              <div className="send-email-preview-head">
                <h4>{isBulk ? 'Preview (first recipient)' : 'Preview'}</h4>
                {previewLoading && <span className="form-hint">Updating…</span>}
              </div>
              {preview ? (
                <>
                  <p>
                    <strong>To:</strong> {preview.to}
                  </p>
                  <p>
                    <strong>Subject:</strong> {preview.subject || '—'}
                  </p>
                  <pre className="send-email-preview-body">
                    {preview.body_text || preview.body || '(empty body)'}
                  </pre>
                </>
              ) : (
                <p className="form-hint">Select a template to preview.</p>
              )}
            </div>
          </>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <LoadingButton
            type="button"
            className="btn btn-primary"
            icon={FiSend}
            loading={sendMutation.isLoading}
            loadingLabel="Sending…"
            disabled={!canSend}
            onClick={() => sendMutation.mutate()}
          >
            {isBulk ? `Send to ${recipientList.length}` : 'Send email'}
          </LoadingButton>
        </div>
      </div>
    </Modal>
  );
}
